import React, { useMemo } from "react";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { FormikProvider, getIn, useFormik } from "formik";
import * as Yup from "yup";
import { IResendRecord } from "../../models/onHandDelivery";
import { EmailRegex, REQUIRED_MSG } from "../../schema/formik";

const MAX_EMAIL_LENGTH = 50;
const MAX_REASON_LENGTH = 150;

// Error messages
const DUPLICATE_PREVIOUS_EMAIL_ERROR =
  "Notice already sent to the party. Please enter another email address.";
const DUPLICATE_FORM_EMAIL_ERROR =
  "An email address already entered in another resend email field.";
const EMPTY_EMAIL_ERROR = "At least one email address is required.";
const INVALID_EMAIL_ERROR = "Invalid Email Address";
const MAX_LENGTH_ERROR = "Maximum 50 characters allowed";

// Styles
const ERROR_BORDER_SX = {
  "& .MuiOutlinedInput-root": { borderColor: "error.main" },
} as const;

/** Form values for editable resend form */
interface ResendFormValues {
  email1: string;
  email2: string;
  email3: string;
  email4: string;
  reason: string;
}

const orderNames = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
];

type OnHandResendProps = {
  resend?: IResendRecord[] | null;
  canResend: boolean;
  resendCount: number;
  onResend?: (data: ResendFormValues) => void | Promise<void>;
  partyEmails?: string[];
  isCleared?: boolean;
};

/** Props for EmailField component */
interface EmailFieldProps {
  label: string;
  name: string;
  value: string;
  disabled: boolean;
  error?: string;
  touched?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/** Props for ReasonField component */
interface ReasonFieldProps {
  name: string;
  value: string;
  disabled: boolean;
  error?: string;
  touched?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// /** Counter for generating unique IDs */
// let emailRowIdCounter = 0;

/**
 * Extracts populated email entries from a resend record
 */
const getPopulatedEmails = (
  record: IResendRecord,
): { label: string; value: string }[] => {
  const emails: { label: string; value: string }[] = [];
  if (record.email1?.trim())
    emails.push({ label: "Email 1", value: record.email1 });
  if (record.email2?.trim())
    emails.push({ label: "Email 2", value: record.email2 });
  if (record.email3?.trim())
    emails.push({ label: "Email 3", value: record.email3 });
  if (record.email4?.trim())
    emails.push({ label: "Email 4", value: record.email4 });
  return emails;
};

/**
 * Creates email field validation with cross-field checks
 */
const createEmailFieldValidation = (
  fieldName: string,
  allPreviousEmails: string[],
) =>
  Yup.string()
    .trim()
    .max(MAX_EMAIL_LENGTH, MAX_LENGTH_ERROR)
    .test("valid-email-if-filled", INVALID_EMAIL_ERROR, (value) => {
      if (!value?.trim()) return true;
      return EmailRegex.test(value.trim());
    })
    .test(
      "not-duplicate-in-form",
      DUPLICATE_FORM_EMAIL_ERROR,
      function (value) {
        const { parent } = this;
        if (!value?.trim()) return true;
        const trimmedValue = value.trim().toLowerCase();
        const emailFields: (keyof ResendFormValues)[] = [
          "email1",
          "email2",
          "email3",
          "email4",
        ];
        return !emailFields.some(
          (field) =>
            field !== fieldName &&
            (parent as ResendFormValues)[field]?.trim().toLowerCase() ===
              trimmedValue,
        );
      },
    )
    .test(
      "not-duplicate-with-previous",
      DUPLICATE_PREVIOUS_EMAIL_ERROR,
      function (value) {
        if (!value?.trim()) return true;
        const trimmedValue = value.trim().toLowerCase();
        return !allPreviousEmails.some(
          (email) => email.trim().toLowerCase() === trimmedValue,
        );
      },
    );

/**
 * EmailField - Reusable component for email input
 */
const EmailField = React.memo(
  ({
    label,
    name,
    value,
    disabled,
    error,
    touched,
    onChange,
    onBlur,
  }: EmailFieldProps) => {
    const hasError = touched && !!error;
    const errorId = `${name}-error`;

    return (
      <Grid item md={3} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel shrink htmlFor={name}>
            {label}
          </InputLabel>
          <TextField
            autoComplete="off"
            id={name}
            name={name}
            placeholder={disabled ? "" : "Enter Email"}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={hasError}
            disabled={disabled}
            variant="outlined"
            inputProps={{
              maxLength: MAX_EMAIL_LENGTH,
              "aria-invalid": hasError ? "true" : "false",
              "aria-describedby": hasError ? errorId : undefined,
              readOnly: disabled,
            }}
            sx={hasError ? ERROR_BORDER_SX : undefined}
          />
        </FormControl>
        {hasError && (
          <span className="form-error" id={errorId} role="alert">
            {error}
          </span>
        )}
      </Grid>
    );
  },
);
EmailField.displayName = "EmailField";

/**
 * ReasonField - Reusable component for reason textarea
 */
const ReasonField = React.memo(
  ({
    name,
    value,
    disabled,
    error,
    touched,
    onChange,
    onBlur,
  }: ReasonFieldProps) => {
    const hasError = touched && !!error;
    const errorId = `${name}-error`;

    return (
      <Grid item xs={12} md={12}>
        <FormControl fullWidth size="small">
          <InputLabel shrink htmlFor={name}>
            Reason for Resending <sup>*</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            id={name}
            name={name}
            placeholder={disabled ? "" : "Enter reason for resending"}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={hasError}
            disabled={disabled}
            variant="outlined"
            // multiline
            // rows={2}
            inputProps={{
              maxLength: MAX_REASON_LENGTH,
              "aria-invalid": hasError ? "true" : "false",
              "aria-describedby": hasError ? errorId : undefined,
              readOnly: disabled,
            }}
            sx={hasError ? ERROR_BORDER_SX : undefined}
          />
        </FormControl>
        {hasError && (
          <span className="form-error" id={errorId} role="alert">
            {error}
          </span>
        )}
      </Grid>
    );
  },
);
ReasonField.displayName = "ReasonField";

/**
 * HistorySection - Renders a single read-only resend history entry
 */
interface HistorySectionProps {
  record: IResendRecord;
  index: number;
}

const HistorySection = React.memo(({ record, index }: Readonly<HistorySectionProps>) => {
  const populatedEmails = getPopulatedEmails(record);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
         {orderNames[index]} Resend {/*  NOSONAR  */}
        </Typography>
      </Grid>
      {populatedEmails.map(({ label, value }) => (
        <EmailField
          key={`history-${index}-${label}`}
          label={label}
          name={`history-${index}-${label.toLowerCase().replace(" ", "")}`}
          value={value}
          disabled={true}
        />
      ))}
      {record.reason?.trim() && (
        <ReasonField
          name={`history-${index}-reason`}
          value={record.reason}
          disabled={true}
        />
      )}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
    </>
  );
});
HistorySection.displayName = "HistorySection";

/**
 * OnHandResend Component
 *
 * A form component for resending emails with history tracking.
 * Features:
 * - Displays previous resend attempts as read-only history
 * - Shows "Resend Notice" button to enable editing (up to 3 attempts max)
 * - When editing, last row is editable with Resend button
 * - Validates unique email addresses and at least one email required
 * - Performance optimized with memoization
 */
function OnHandResend({
  resend,
  resendCount,
  canResend,
  onResend,
  partyEmails = [],
  isCleared
}: Readonly<OnHandResendProps>) {
  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  // Normalize resend array
  const resendHistory = useMemo(() => resend ?? [], [resend]);

  const allPreviousEmails = useMemo(() => {
    const emailsFromHistory = resendHistory.flatMap((record) =>
      getPopulatedEmails(record).map((e) => e.value.trim()),
    );
    const uniqueEmails = new Set([
      ...partyEmails.map((e) => e.trim().toLowerCase()),
      ...emailsFromHistory.map((e) => e.toLowerCase()),
    ]);
    return Array.from(uniqueEmails);
  }, [resendHistory, partyEmails]);

  /**
   * Yup validation schema for resend form
   */
  const resendValidationSchema = useMemo(
    () =>
      Yup.object({
        email1: createEmailFieldValidation("email1", allPreviousEmails).test(
          "at-least-one-email",
          EMPTY_EMAIL_ERROR,
          (value, context) => {
            const { email2, email3, email4 } = context.parent as ResendFormValues;
            // Show error on email1 if no emails are filled
            return !!(
              value?.trim() ||
              email2?.trim() ||
              email3?.trim() ||
              email4?.trim()
            );
          }
        ),
        email2: createEmailFieldValidation("email2", allPreviousEmails),
        email3: createEmailFieldValidation("email3", allPreviousEmails),
        email4: createEmailFieldValidation("email4", allPreviousEmails),
        reason: Yup.string().trim().required(REQUIRED_MSG).max(MAX_REASON_LENGTH, `Maximum ${MAX_REASON_LENGTH} characters allowed`),
      }),
    [allPreviousEmails],
  );

  // Handler to start editing mode
  const handleResendNoticeClick = React.useCallback(() => {
    setIsEditing(true);
  }, []);

  const formik = useFormik<ResendFormValues>({
    initialValues: {
      email1: "",
      email2: "",
      email3: "",
      email4: "",
      reason: "",
    },
    validationSchema: resendValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, helpers) => {
      try {
        await Promise.resolve(onResend?.(values));
        helpers.resetForm();
        setIsEditing(false);
      } catch (error) {
        console.error("Error resending notice:", error);
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleBlur, handleChange, isSubmitting } =
    formik;

  // Handle form submit with validation
  const handleResendSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      formik.validateForm().then((validationErrors) => {
        formik.setTouched({
          email1: true,
          email2: true,
          email3: true,
          email4: true,
          reason: true,
        });
        if (Object.keys(validationErrors).length === 0) {
          formik.handleSubmit();
        }
      });
    },
    [formik],
  );

  // Handler to cancel editing mode
  const handleCancelClick = React.useCallback(() => {
    formik.resetForm();
  }, [formik]);

  // Email field configurations for DRY rendering
  const emailFields = useMemo(
    () =>
      [
        { label: "Email 1", name: "email1" as const },
        { label: "Email 2", name: "email2" as const },
        { label: "Email 3", name: "email3" as const },
        { label: "Email 4", name: "email4" as const },
      ] as const,
    [],
  );

  const resendHistoryWithKeys = useMemo(() => {
    const duplicateCounts = new Map<string, number>();

    return resendHistory.map((record) => {
      const signature = [
        record.email1?.trim() ?? "",
        record.email2?.trim() ?? "",
        record.email3?.trim() ?? "",
        record.email4?.trim() ?? "",
        record.reason?.trim() ?? "",
      ].join("|");

      const currentCount = duplicateCounts.get(signature) ?? 0;
      duplicateCounts.set(signature, currentCount + 1);

      return {
        record,
        key: `history-${signature || "empty"}-${currentCount}`,
      };
    });
  }, [resendHistory]);

  return (
    <div className="accordion-container">
      <FormikProvider value={formik}>
        <Grid container spacing={2} className="filter-margin2">
          {/* Render history sections (read-only previous resends) */}
          {resendHistoryWithKeys.map(({ record, key }, index) => (
            <HistorySection
              key={key}
              record={record}
              index={index}
            />
          ))}

          {/* Show "Resend Notice" button when not editing and can still resend */}
          {canResend && !isEditing && !isCleared && (
            <Grid item xs={12}>
              <div className="btn-group-submit">
                <Button
                  type="button"
                  variant="contained"
                  className="entry ml"
                  data-testid="resend-notice-button"
                  onClick={handleResendNoticeClick}
                >
                  Resend Notice
                </Button>
              </div>
            </Grid>
          )}

          {/* Render editable form only when in editing mode and resend attempts < 3 */}
          {canResend && isEditing && (
            <Grid item xs={12}>
              <form onSubmit={handleResendSubmit} noValidate>
                <Grid container spacing={2} className="filter-margin1">
                  {resendCount >= 0 && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {orderNames[resendCount]} Resend
                      </Typography>
                    </Grid>
                  )}

                  {/* Email Fields */}
                  {emailFields.map(({ label, name }) => (
                    <EmailField
                      key={name}
                      label={label}
                      name={name}
                      value={values[name]}
                      disabled={false}
                      error={getIn(errors, name)}
                      touched={getIn(touched, name)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ))}

                  {/* Reason for Resending */}
                  <ReasonField
                    name="reason"
                    value={values.reason}
                    disabled={false}
                    error={getIn(errors, "reason")}
                    touched={getIn(touched, "reason")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  {/* Error and Submit Button */}
                  <Grid item xs={12}>
                    <div className="btn-group-submit">
                      <Button
                        type="button"
                        variant="contained"
                        className="clear-btn mr-0 ml"
                        onClick={handleCancelClick}
                        data-testid="cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        className="entry ml"
                        disabled={isSubmitting}
                        data-testid="resend-button"
                        aria-busy={isSubmitting}
                      >
                        {isSubmitting ? "Resending..." : "Resend Notice"}
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}

          {/* Show message when max resends reached */}
          {/* {!canResend && resendCount > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Maximum resend attempts ({MAX_RESEND_ATTEMPTS}) reached.
              </Typography>
            </Grid>
          )} */}
        </Grid>
      </FormikProvider>
    </div>
  );
}

export default OnHandResend;
export type { ResendFormValues };
